import $ from "jquery";
import * as preact from "preact";
import * as _ from "underscore";

import * as editCardDialog from "editCardDialog";
import loadHandwritingFont from "loadHandwritingFont";
import * as modalController from "modalController";
import { shownAgain, type PreactPageProps } from "pages/PreactPage";
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as sourceUtils from "shared/sourceUtils";
import type { UserWordsSearchQuery } from "stores/RemoteUserWordsStore";
import stores from "stores/stores";
import UserWordStore from "stores/UserWordStore";
import translateErrorHandler from "translateErrorHandler";
import Button from "ui/Button";
import SearchField from "ui/SearchField";
import Dialog from "views/Dialog";
import ReadFirstHintView from "views/ReadFirstHintView";
import { RemoteWordListToolbar } from "views/RemoteWordListToolbarView";
import { RemoteWordList } from "views/RemoteWordListView";
import SkeletonView from "views/SkeletonView";
import SourcePickerModal from "views/SourcePickerModal";
import * as walkthrough from "walkthrough";

interface SourceItem {
  source: string;
  title?: string;
  author?: string;
}

interface AddWordSuggestion {
  word: string;
  type: string;
  translation: string;
  currentLearningLanguage: string;
  firstLanguage: string;
}

interface State {
  addWordSuggestionOne?: AddWordSuggestion;
  addWordSuggestionTwo?: AddWordSuggestion;
  didYouMean?: string;
  emptyFirstUse: boolean;
  extraSidebarSource?: string;
  initialWordsFailed: boolean;
  initialWordsFetched: boolean;
  searchInput: string;
  searchStatusHtml?: string;
  selectedFilter?: string;
  sources: SourceItem[];
  sourcesLoaded: boolean;
  /** The search term the current add-word suggestions were looked up for. */
  suggestionsSearchTerm?: string;
}

const MAX_SIDEBAR_SOURCES = 30;

const clearedSuggestions: Pick<
  State,
  "addWordSuggestionOne" | "addWordSuggestionTwo" | "suggestionsSearchTerm"
> = {
  addWordSuggestionOne: undefined,
  addWordSuggestionTwo: undefined,
  suggestionsSearchTerm: undefined,
};

const wordGroupFilterMap = {
  all: {},
  allPublic: {
    public: true,
  },
  favorite: {
    favorite: true,
  },
  notFavorite: {
    favorite: false,
  },
  readyToTest: {
    wordGroup: "readyToTest",
  },
  notStarted: {
    wordGroup: "notStarted",
  },
  scheduled: {
    wordGroup: "scheduled",
  },
  learning: {
    wordGroup: "learning",
  },
  learned: {
    wordGroup: "mastered",
  },
  deleted: {
    wordGroup: "deleted",
    sortBy: "deletedDate",
  },
  book(data) {
    return {
      bookIDs: data,
    };
  },
  source(data) {
    return {
      source: data,
    };
  },
};

/** The sidebar's filters, also in the sort menu where there's no sidebar */
const staticFilters = [
  { value: "all", label: "All Words" },
  { value: "favorite", label: "Starred" },
  { value: "notFavorite", label: "Not Starred" },
  { value: "scheduled", label: "Scheduled" },
  { value: "notStarted", label: "Not Started" },
  { value: "learning", label: "Learning" },
  { value: "learned", label: "Learned" },
  { value: "deleted", label: "Deleted" },
];

/** The menu's choice opening the source picker */
const chooseSourceValue = "chooseSource";

const getWordGroupFilter = (checkedValue?: string) => {
  const filter = (checkedValue || "all").split("-");
  const wordGroup = wordGroupFilterMap[filter[0]];

  if (typeof wordGroup === "function") {
    return wordGroup(filter[1]);
  }
  return wordGroup;
};

const isPremiumPlan = () =>
  stores.user.state.value.subscriptionPlan === "standard" ||
  stores.user.state.value.subscriptionPlan === "plus" ||
  stores.user.state.value.subscriptionPlan === "freeForever";

export default class WordsPageView extends preact.Component<
  PreactPageProps,
  State
> {
  private disposeLanguageWatch?: () => void;
  private disposeSearchQueryWatch?: () => void;
  private mounted = false;
  private pendingQuickSaves = new Set<AddWordSuggestion>();
  /**
   * Identifies the search context the current suggestions belong to. Every
   * path that replaces, clears, or moves on from them (typing in the search
   * input, a new search, a quick save, a filter, language, or search-query
   * change) bumps this, so async completions (lookups, spelling checks, quick
   * saves) can tell the user has moved on and must leave the newer state
   * alone.
   */
  private searchGeneration = 0;
  private searchInputRef = preact.createRef<HTMLInputElement>();
  /**
   * The words' search and filter as this page was left. Its store is the
   * reader's too (its sidebar's Words tab, the end of a text), which lists one
   * text's words through it meanwhile.
   */
  private searchQueryLeft?: UserWordsSearchQuery;
  /**
   * …and its words, which the reader adds to as it translates, whatever this
   * page's filter
   */
  private wordsLeft?: UserWordStore[];

  state: State = {
    emptyFirstUse: false,
    initialWordsFailed: false,
    initialWordsFetched: false,
    searchInput: stores.remoteUserWords.searchQuery.value.search ?? "",
    selectedFilter: "all",
    sources: [],
    sourcesLoaded: false,
  };

  componentDidMount() {
    this.mounted = true;
    loadHandwritingFont();
    if (stores.user.state.value.readerTranslationFont === "normal") {
      $("body").addClass("normalTranslationFont");
    }
    $("title").text("Words - Readlang");

    this.disposeSearchQueryWatch = stores.remoteUserWords.onSearchQueryChange(
      this.onChangeSearchQuery
    );
    this.disposeLanguageWatch = stores.user.onChange(
      (attributes) => attributes.currentLearningLanguage,
      this.onLanguageChange
    );

    this.fetchPageData();
  }

  componentDidUpdate(previousProps: PreactPageProps) {
    if (previousProps.active && !this.props.active) {
      this.searchQueryLeft = stores.remoteUserWords.searchQuery.value;
      this.wordsLeft = stores.remoteUserWords.words.value;
    }
    if (!shownAgain(this.props, previousProps)) {
      return;
    }
    // Back from another page (the tab kept as left), or its tab reselected
    const searchQueryLeft = this.searchQueryLeft;
    this.searchQueryLeft = undefined;
    if (
      searchQueryLeft &&
      (stores.remoteUserWords.searchQuery.value !== searchQueryLeft ||
        stores.remoteUserWords.words.value !== this.wordsLeft)
    ) {
      // The reader listed one text's words meanwhile, or translated more
      // (a new user's first among them)
      this.reloadWords(searchQueryLeft);
      return;
    }
    // Words translated since are listed at the top, so it's refreshed (as
    // filtered and searched) if that's where it's scrolled (as a reselected
    // tab is)
    requestAnimationFrame(() => {
      if (window.scrollY < 100) {
        void stores.remoteUserWords.reFetch().catch(() => {});
        void this.fetchSources().catch(() => {});
      }
    });
  }

  /**
   * Its words afresh, filtered and searched as `searchQuery`, from the top
   * and behind the placeholder, as when the page opens
   */
  private reloadWords(searchQuery: UserWordsSearchQuery) {
    this.setState({ emptyFirstUse: false, initialWordsFetched: false });
    requestAnimationFrame(() => window.scrollTo(0, 0));
    stores.remoteUserWords.resetSearchQuery(searchQuery);
    stores.remoteUserWords.reFetch().then(
      () => this.setStateIfMounted({ initialWordsFetched: true }),
      () => this.setStateIfMounted({ initialWordsFailed: true })
    );
    void this.fetchSources().catch(() => {});
  }

  componentWillUnmount() {
    this.mounted = false;
    this.searchGeneration += 1;
    this.disposeLanguageWatch?.();
    this.disposeLanguageWatch = undefined;
    this.disposeSearchQueryWatch?.();
    this.disposeSearchQueryWatch = undefined;
  }

  private setStateIfMounted<K extends keyof State>(
    state:
      | Pick<State, K>
      | State
      | ((
          previousState: Readonly<State>,
          props: Readonly<PreactPageProps>
        ) => Pick<State, K> | State | null)
  ) {
    if (this.mounted) {
      this.setState(state as any);
    }
  }

  private onLanguageChange = () => {
    this.searchGeneration += 1;
    this.setStateIfMounted({
      ...clearedSuggestions,
      didYouMean: undefined,
      emptyFirstUse: false,
      extraSidebarSource: undefined,
      initialWordsFailed: false,
      initialWordsFetched: false,
      searchInput: "",
      searchStatusHtml: undefined,
      selectedFilter: "all",
      sources: [],
      sourcesLoaded: false,
    });
    this.fetchPageData();
  };

  private onChangeSearchQuery = () => {
    this.searchGeneration += 1;
    const newSearchQuery =
      stores.remoteUserWords.searchQuery.value.search ?? "";
    this.setStateIfMounted({
      ...clearedSuggestions,
      didYouMean: undefined,
      searchInput: newSearchQuery,
      searchStatusHtml: undefined,
    });
  };

  // On terminal failure the fetch promises reject (readlangClient has
  // already shown a global notification) — swap the blank page / eternal
  // sidebar throbber for a recoverable state.
  private fetchPageData() {
    void this.fetchInitialWords().catch(() => {
      this.setStateIfMounted({ initialWordsFailed: true });
    });
    void this.fetchSources().catch(() => {
      this.setStateIfMounted({ sourcesLoaded: true });
    });
  }

  private fetchInitialWords() {
    return stores.remoteUserWords.fetch({
      success: (_error, group) => {
        if (!this.mounted) {
          return;
        }
        if (group.count === 0 && !walkthrough.hasAchievement("translate")) {
          this.setState({
            emptyFirstUse: true,
            initialWordsFetched: true,
          });
          return;
        }

        this.setState({
          emptyFirstUse: false,
          initialWordsFetched: true,
          searchInput: stores.remoteUserWords.searchQuery.value.search ?? "",
          selectedFilter: "all",
        });
      },
    });
  }

  private fetchSources() {
    const language = stores.user.currentLearningLanguage.value;
    return readlangClient.request(
      "GET",
      "/api/sourcesWithWords",
      {
        language,
      },
      (sources) => {
        this.setStateIfMounted({
          sources,
          sourcesLoaded: true,
        });
      }
    );
  }

  private importWords = () => {
    this.props.appRouter.navigate("importWords", { trigger: true });
  };

  private clearSearch = () => {
    stores.remoteUserWords.patchSearchQuery({ search: "" });
  };

  private newManualUserWord(word: string, translation: string) {
    return new UserWordStore({
      word,
      translation,
      contexts: [
        {
          text: word,
          source: "manual",
        },
      ],
      language: stores.user.currentLearningLanguage.value,
    });
  }

  private showAddWordDialog(word: string, translation: string) {
    editCardDialog.show(this.newManualUserWord(word, translation), {
      title: `Add New ${/ /.test(word) ? "Phrase" : "Word"}`,
      // Clearing the search (rather than resetting the whole query) keeps
      // the active sidebar filter, matching the quick-add path. The refetch
      // happens via the store's search-query-change effect.
      savedCallback: () => this.clearSearch(),
      newCard: true,
    });
  }

  /**
   * Saves a suggested word straight away, skipping the Add New Word dialog, then
   * clears and refocuses the search box ready for the next word.
   */
  private quickAddWord = async (suggestion: AddWordSuggestion) => {
    // setState only takes effect on re-render, so this set is what stops a
    // repeated Shift+Return (or double shift-click) from saving the same word
    // twice. It's keyed per suggestion so a save of a newly looked-up word
    // isn't blocked by an earlier one still in flight.
    if (this.pendingQuickSaves.has(suggestion)) {
      return;
    }
    this.pendingQuickSaves.add(suggestion);

    const {
      addWordSuggestionOne,
      addWordSuggestionTwo,
      suggestionsSearchTerm,
    } = this.state;

    // Invalidate any in-flight lookup for the term being saved.
    const saveID = ++this.searchGeneration;
    this.setState({ ...clearedSuggestions, didYouMean: undefined });

    try {
      await this.newManualUserWord(
        suggestion.word,
        suggestion.translation
      ).save();
    } catch {
      // readlangClient already notifies the user, so just allow a retry —
      // unless the user has since typed something else or moved on to another
      // search or filter (each of which bumps the generation), in which case
      // restoring these suggestions would clobber the newer state.
      if (this.mounted && saveID === this.searchGeneration) {
        this.setState({
          addWordSuggestionOne,
          addWordSuggestionTwo,
          suggestionsSearchTerm,
        });
      }
      return;
    } finally {
      this.pendingQuickSaves.delete(suggestion);
    }

    // A stale generation means the user has moved on — including typing their
    // next word while the save was in flight, which must not be wiped here.
    if (!this.mounted || saveID !== this.searchGeneration) {
      return;
    }
    this.clearSearch();
    this.searchInputRef.current?.focus();
  };

  /**
   * The suggestion that Shift+Return saves, if the search box still holds the
   * term those suggestions were looked up for.
   */
  private quickAddSuggestion(inputValue: string) {
    if (inputValue.trim() !== this.state.suggestionsSearchTerm) {
      return undefined;
    }
    return this.state.addWordSuggestionOne ?? this.state.addWordSuggestionTwo;
  }

  private lookupPhrase = async (searchTerm: string, searchID: number) => {
    const source = stores.user.currentLearningLanguage.value;
    const target = stores.user.firstLanguage.value;

    try {
      const [sourceToTarget, targetToSource] = await Promise.all([
        new Promise<any>((resolve, reject) => {
          readlangClient.request(
            "POST",
            "/api/translate",
            {
              source,
              target,
              q: searchTerm,
            },
            (response) => {
              resolve(response);
            },
            (error) => {
              reject(error);
            }
          );
        }),
        new Promise<any>((resolve, reject) => {
          readlangClient.request(
            "POST",
            "/api/translate",
            {
              source: target,
              target: source,
              q: searchTerm,
            },
            (response) => {
              resolve(response);
            },
            (error) => {
              reject(error);
            }
          );
        }),
      ]);

      if (!this.mounted || searchID !== this.searchGeneration) {
        return;
      }

      let gotTranslation = false;
      let addWordSuggestionOne: AddWordSuggestion | undefined;
      let addWordSuggestionTwo: AddWordSuggestion | undefined;

      if (
        sourceToTarget.translations &&
        sourceToTarget.translations[0] !== searchTerm
      ) {
        gotTranslation = true;
        const translation = sourceToTarget.translations[0];
        addWordSuggestionOne = {
          word: searchTerm,
          type: / /.test(searchTerm) ? "Phrase" : "Word",
          translation,
          currentLearningLanguage: languageMap.map[source].english,
          firstLanguage: languageMap.map[target].english,
        };

        readlangClient.request(
          "GET",
          "/api/smartDictionary/checkSpelling",
          {
            word: searchTerm,
            translation,
            language: source,
            translationLanguage: target,
          },
          (response) => {
            if (
              this.mounted &&
              searchID === this.searchGeneration &&
              !response.correct
            ) {
              this.setState({ didYouMean: response.corrected });
            }
          }
        );
      }

      if (
        targetToSource.translations &&
        targetToSource.translations[0] !== searchTerm
      ) {
        const translatedWord = targetToSource.translations[0];
        gotTranslation = true;
        addWordSuggestionTwo = {
          word: translatedWord,
          type: / /.test(translatedWord) ? "Phrase" : "Word",
          translation: searchTerm,
          currentLearningLanguage: languageMap.map[source].english,
          firstLanguage: languageMap.map[target].english,
        };
      }

      if (!gotTranslation) {
        addWordSuggestionOne = {
          word: searchTerm,
          type: / /.test(searchTerm) ? "Phrase" : "Word",
          translation: searchTerm,
          currentLearningLanguage: languageMap.map[source].english,
          firstLanguage: languageMap.map[target].english,
        };
      }

      this.setState({
        addWordSuggestionOne,
        addWordSuggestionTwo,
        suggestionsSearchTerm: searchTerm,
      });
    } catch (err) {
      if (!this.mounted || searchID !== this.searchGeneration) {
        return;
      }
      this.setState({
        ...clearedSuggestions,
        searchStatusHtml: undefined,
      });
      // On a terminal failure (network loss, 5xx) readlangClient already
      // showed a notification bar, so skip the domain error handling.
      if (!err?.alreadyNotified) {
        translateErrorHandler(err, stores, (options) => {
          this.setStateIfMounted({ searchStatusHtml: options.htmlMessage });
        });
      }
    }
  };

  private search = (searchTerm?: string) => {
    const inputValue =
      searchTerm ??
      this.searchInputRef.current?.value ??
      this.state.searchInput;
    const trimmedSearchTerm = inputValue.trim();

    // Patching (rather than resetting) the store query keeps any active
    // sidebar filter, so the search runs within the filtered subset. Setting
    // the store query fires onChangeSearchQuery synchronously, which clears
    // any existing suggestions and bumps searchGeneration — so this search's
    // generation must be captured afterwards.
    stores.remoteUserWords.patchSearchQuery(
      { search: trimmedSearchTerm },
      { suppressFetch: true }
    );
    const searchID = this.searchGeneration;

    this.setState({
      searchInput: trimmedSearchTerm,
    });

    void stores.remoteUserWords.reFetch({
      success: () => {
        if (!this.mounted || searchID !== this.searchGeneration) {
          return;
        }

        const lowerCaseWords = _.map(
          stores.remoteUserWords.getFieldValues("word"),
          (word) => word.toLowerCase()
        );

        const lowerCaseTranslations = _.map(
          stores.remoteUserWords.getFieldValues("translation"),
          (translation) => translation.toLowerCase()
        );

        const lowerCaseSearchTerm = trimmedSearchTerm.toLowerCase();

        const exactWordMatch = _.contains(lowerCaseWords, lowerCaseSearchTerm);
        const exactTranslationMatch = _.contains(
          lowerCaseTranslations,
          lowerCaseSearchTerm
        );

        if (!exactWordMatch && !exactTranslationMatch) {
          void this.lookupPhrase(trimmedSearchTerm, searchID);
        }
      },
    });
  };

  private onSearchInput = (searchInput: string) => {
    // Editing the input moves the user on from the current suggestions, so
    // clear them and bump the generation: neither an in-flight lookup or
    // spelling check nor a failed quick save may deposit or restore cards for
    // the old term beneath the newer one, where they'd be immediately
    // actionable against the wrong word.
    this.searchGeneration += 1;
    this.setState({
      ...clearedSuggestions,
      didYouMean: undefined,
      searchInput,
    });
  };

  private keyDownSearch = (event: KeyboardEvent) => {
    this.setState({ searchStatusHtml: undefined });
    // Shift+Return quick-saves the suggested word (rather than searching)
    if (event.key === "Enter" && event.shiftKey) {
      const suggestion = this.quickAddSuggestion(
        this.searchInputRef.current?.value ?? this.state.searchInput
      );
      if (suggestion) {
        event.preventDefault();
        void this.quickAddWord(suggestion);
      }
    }
  };

  private selectDidYouMean = (event: MouseEvent, corrected: string) => {
    event.preventDefault();
    event.stopPropagation();

    this.search(corrected);
  };

  private handleFilterChange = (value: string) => {
    this.setState({
      ...clearedSuggestions,
      selectedFilter: value,
      searchStatusHtml: undefined,
      didYouMean: undefined,
    });
    // Carry any active search into the new filter, so filters narrow the
    // search results rather than clearing them. Must be read before fetch(),
    // which resets the query.
    const { search } = stores.remoteUserWords.searchQuery.value;
    stores.remoteUserWords.fetch({
      data: {
        ...(search ? { search } : {}),
        ...getWordGroupFilter(value),
      },
    });
  };

  private blockSourceFilterForFreePlan = (event: MouseEvent) => {
    if (isPremiumPlan()) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    this.showSourceFilterUpsell();
  };

  private showSourceFilterUpsell() {
    premiumUpsellModal.show({
      title: "booksFilter",
      heading: "Upgrade to filter by source...",
      showTitle: false,
    });
  }

  private selectSource = (source: string) => {
    modalController.cancel();
    const visibleSource = this.visibleSources().some(
      (item) => item.source === source
    );

    this.setState(
      {
        extraSidebarSource: visibleSource ? undefined : source,
      },
      () => {
        this.handleFilterChange(`source-${source}`);
      }
    );
  };

  private showAllSources = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.showSourcePicker();
  };

  private showSourcePicker() {
    modalController.show(
      <Dialog heading="Filter by Source">
        <SourcePickerModal
          sources={this.state.sources}
          onSelect={this.selectSource}
        />
      </Dialog>
    );
  }

  /** The sidebar's filters, for the sort menu on screens without it */
  private menuFilters() {
    const { selectedFilter = "all", sources } = this.state;
    const source = selectedFilter.startsWith("source-")
      ? sources.find((item) => `source-${item.source}` === selectedFilter)
      : undefined;
    return {
      options: [
        ...staticFilters,
        source
          ? {
              value: selectedFilter,
              label: sourceUtils.formatSourceDisplayName(
                source.source,
                source.title,
                source.author
              ),
            }
          : { value: chooseSourceValue, label: "From a source…" },
      ],
      selected: selectedFilter,
      onSelect: (value: string) => {
        if (value !== chooseSourceValue) {
          this.handleFilterChange(value);
        } else if (isPremiumPlan()) {
          this.showSourcePicker();
        } else {
          this.showSourceFilterUpsell();
        }
      },
    };
  }

  private visibleSources() {
    const visibleSources = this.state.sources.slice(0, MAX_SIDEBAR_SOURCES);
    if (
      this.state.extraSidebarSource &&
      !visibleSources.some(
        (item) => item.source === this.state.extraSidebarSource
      )
    ) {
      const item = this.state.sources.find(
        (source) => source.source === this.state.extraSidebarSource
      );
      if (item) {
        return visibleSources.concat(item);
      }
    }
    return visibleSources;
  }

  private renderAddWordButton(
    className: string,
    suggestion?: AddWordSuggestion
  ) {
    return (
      <div
        class={`addWordButton ${className} prominent`}
        title={
          suggestion
            ? `Shift-click${
                suggestion === this.quickAddSuggestion(this.state.searchInput)
                  ? " (or Shift+Return)"
                  : ""
              } to save this ${suggestion.type.toLowerCase()} immediately, skipping the details dialog`
            : undefined
        }
        onClick={(event) => {
          if (!suggestion) {
            return;
          }
          if (event.shiftKey) {
            void this.quickAddWord(suggestion);
          } else {
            this.showAddWordDialog(suggestion.word, suggestion.translation);
          }
        }}
        style={{ display: suggestion ? "inline-block" : "none" }}
      >
        {suggestion ? (
          <>
            <h3>
              <i class="icon-plus"></i> Add {suggestion.type}
            </h3>
            <p>
              {suggestion.word}{" "}
              <small>({suggestion.currentLearningLanguage})</small>
            </p>
            <p>
              {suggestion.translation}{" "}
              <small>({suggestion.firstLanguage})</small>
            </p>
          </>
        ) : null}
      </div>
    );
  }

  private renderEmptyFirstUseHint() {
    return (
      <ReadFirstHintView>
        The words and phrases you translate while reading will be listed here,
        for you to review and edit.
      </ReadFirstHintView>
    );
  }

  private renderStaticSidebarItem(id: string, value: string, label: string) {
    return (
      <li key={value}>
        <input
          id={id}
          type="radio"
          name="wordFilter"
          value={value}
          checked={this.state.selectedFilter === value}
          onChange={() => this.handleFilterChange(value)}
        />
        <label for={id}>{label}</label>
      </li>
    );
  }

  private renderSourceItem(item: SourceItem) {
    const displayName = sourceUtils.formatSourceDisplayName(
      item.source,
      item.title,
      item.author
    );
    const emoji = sourceUtils.getSourceEmoji(item.source);
    const sidebarId =
      "sidebar-source-" + item.source.replace(/[^a-zA-Z0-9]/g, "_");
    const value = `source-${item.source}`;

    return (
      <li key={item.source}>
        <input
          id={sidebarId}
          type="radio"
          name="wordFilter"
          value={value}
          checked={this.state.selectedFilter === value}
          onClick={this.blockSourceFilterForFreePlan}
          onChange={() => this.handleFilterChange(value)}
        />
        <label
          for={sidebarId}
          title={displayName}
          onClick={this.blockSourceFilterForFreePlan}
        >
          <span class="sourceEmoji">{emoji}</span>
          {displayName}
        </label>
      </li>
    );
  }

  private renderWordsPage() {
    if (this.state.initialWordsFailed) {
      return (
        <p>
          Couldn't load your words.{" "}
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              // Retry the source list too — during an outage it fails
              // alongside the words and would otherwise stay empty forever
              this.setState({
                initialWordsFailed: false,
                sourcesLoaded: false,
              });
              this.fetchPageData();
            }}
          >
            Try again
          </a>
        </p>
      );
    }

    if (!this.state.initialWordsFetched) {
      return <SkeletonView kind="rows" count={10} />;
    }

    return (
      <>
        <div class="wordsPageTopControls">
          <div class="searchLabel"></div>
          <SearchField
            value={this.state.searchInput}
            query={stores.remoteUserWords.searchQuery.value.search ?? ""}
            placeholder="Lookup word or phrase"
            inputRef={this.searchInputRef}
            onInput={this.onSearchInput}
            onKeyDown={this.keyDownSearch}
            onSearch={(term) => (term ? this.search(term) : this.clearSearch())}
          />
          <Button
            kind="primary"
            id="importWordsButton"
            icon="icon-circle-arrow-up"
            aria-label="Import Words"
            onClick={this.importWords}
          >
            <span class="noMobile">Import Words</span>
          </Button>
        </div>
        <div class="wordsPageTopControls">
          <div class="searchLabel"></div>
          <div class="addWordContainer">
            {/* searchStatusHtml is provided by translateErrorHandler and may
                contain anchor elements that have jQuery click handlers
                attached after rendering (notably #phraseLengthUpsell). */}
            <div
              class="searchStatus"
              style={{
                display: this.state.searchStatusHtml ? undefined : "none",
              }}
              dangerouslySetInnerHTML={{
                __html: this.state.searchStatusHtml ?? "",
              }}
            ></div>
            <div
              class="didYouMeanLink"
              style={{ display: this.state.didYouMean ? undefined : "none" }}
            >
              {this.state.didYouMean ? (
                <a
                  href="#"
                  style="line-height:2.5"
                  onClick={(event) =>
                    this.selectDidYouMean(event, this.state.didYouMean!)
                  }
                >
                  Did you mean "{this.state.didYouMean}"
                </a>
              ) : null}
            </div>
            {this.renderAddWordButton("one", this.state.addWordSuggestionOne)}
            {this.renderAddWordButton("two", this.state.addWordSuggestionTwo)}
          </div>
        </div>

        <div class="categorySelectorView"></div>

        <div>
          <div class="leftColumn"></div>
          <div class="rightColumn">
            <div class="wordsToolbarContainer">
              <div class="wordsToolbar">
                <RemoteWordListToolbar
                  stores={stores}
                  model={stores.remoteUserWords}
                  appRouter={this.props.appRouter}
                  filters={this.menuFilters()}
                />
              </div>
            </div>
          </div>
        </div>

        <div class="sidebar leftColumn">
          <ul class="sidebarList">
            {staticFilters.map(({ value, label }) =>
              this.renderStaticSidebarItem(`sidebar-${value}`, value, label)
            )}
            <hr />
          </ul>
          <ul class="sidebarList sources">
            {!this.state.sourcesLoaded ? (
              <li>
                <img
                  class="sourcesWithWordsThrobber"
                  src="/content/throbber.gif"
                />
              </li>
            ) : null}
            {this.visibleSources().map((source) =>
              this.renderSourceItem(source)
            )}
            {this.state.sources.length > MAX_SIDEBAR_SOURCES ? (
              <li class="showAllSources">
                <a href="#" onClick={this.showAllSources}>
                  Show all...
                </a>
              </li>
            ) : null}
          </ul>
        </div>

        <div class="mainWordList rightColumn">
          <RemoteWordList
            stores={stores}
            model={stores.remoteUserWords}
            wordGroup=""
            showCancelButton={false}
          />
        </div>
      </>
    );
  }

  render() {
    if (this.state.emptyFirstUse) {
      return this.renderEmptyFirstUseHint();
    }

    return this.renderWordsPage();
  }
}
